import { SentryLink } from 'apollo-link-sentry'
import { createUploadLink } from 'apollo-upload-client'
import axios from 'axios'
import { getCookie } from 'cookies-next'

import {
  ApolloClient,
  DefaultOptions,
  InMemoryCache,
  from,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { RetryLink } from '@apollo/client/link/retry'
import { offsetLimitPagination } from '@apollo/client/utilities'
import * as axiosFetch from '@lifeomic/axios-fetch'
import get from 'lodash/get'

const httpLink = createUploadLink({
  // never attach cookies to the API request: the backend allows every origin,
  // so a credentialed grant would let any site read this endpoint as the
  // logged-in user. Auth travels in the Bearer header built by authLink below.
  credentials: 'omit',
  fetch: axiosFetch.buildAxiosFetch(axios, (config, _input, init) => ({
    ...config,
    onUploadProgress: get(init, 'onUploadProgress', undefined),
  })),
  uri: `${process.env.NEXT_PUBLIC_BACKEND_API_BASE}/graphql`,
})

const authLink = setContext((_, { headers }) => {
  // read the auth token from the access_token cookie via document.cookie —
  // possible only because every Set-Cookie for it uses httpOnly: false. The
  // cookie is scoped to the page host (lib/domain.ts), so page JS can always
  // read it and rebuild the header, including on workspace custom domains
  // where the cookie never reaches the API host on its own.
  const token = getCookie('access_token')

  if (Boolean(token)) {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    }
  }

  // return the headers to the context so httpLink can read them
  return {
    headers,
  }
})

const defaultOptions: DefaultOptions = {
  watchQuery: {
    // on SSR avoid using cache: https://github.com/apollographql/apollo-client/issues/9066
    fetchPolicy:
      typeof window !== 'undefined' ? 'no-cache' : 'cache-and-network',
    errorPolicy: 'ignore',
  },

  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },

  mutate: {
    errorPolicy: 'all',
  },
}

const cache = new InMemoryCache({
  resultCaching: typeof window !== 'undefined',
  typePolicies: {
    Query: {
      fields: {
        // https://stackoverflow.com/questions/67441707/combine-offsetlimitpagination-with-filtering-apollo-graphql
        elements: offsetLimitPagination([
          'createdByMe',
          'workspaceUserId',
          'filterByElementType',
          'search',
          'sort',
          'tags',
          'contentTypes',
          'createdBy',
        ]),
        totalElements: {
          keyArgs: [
            'createdByMe',
            'workspaceUserId',
            'filterByElementType',
            'search',
            'tags',
            'contentTypes',
            'createdBy',
          ],
        },
      },
    },
  },
  possibleTypes: {
    Collection: ['MixedCollection'],
    Content: [
      'AudioContent',
      'EventContent',
      'ImageContent',
      'LinkContent',
      'PdfContent',
      'QuizContent',
      'ScormContent',
      'TextContent',
      'VideoContent',
    ],
    Element: [
      'AudioContent',
      'EventContent',
      'ImageContent',
      'LinkContent',
      'MixedCollection',
      'PdfContent',
      'QuizContent',
      'ScormContent',
      'TextContent',
      'VideoContent',
    ],
    QuizContentQuestion: [
      'QuizContentQuestionEssay',
      'QuizContentQuestionMatching',
      'QuizContentQuestionMissingWord',
      'QuizContentQuestionMultipleChoice',
      'QuizContentQuestionNumerical',
      'QuizContentQuestionSingleChoice',
      'QuizContentQuestionTrueFalse',
    ],
  },
})

const retryLink = new RetryLink({
  delay: {
    initial: 200,
    max: 200,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => !!error,
  },
})

export default new ApolloClient({
  link: from([
    retryLink,
    authLink,
    new SentryLink({
      attachBreadcrumbs: {
        includeQuery: true,
        includeVariables: true,
        includeError: true,
      },
    }),
    httpLink,
  ]),
  cache,
  defaultOptions,
  ssrMode: false,
})
